import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('pharmax_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname.startsWith('/app')) {
      console.warn('Session expired, redirecting to login');
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  updateProfile: (data) => api.patch('/auth/me', data),
};

// Projects
export const projectsAPI = {
  list: () => api.get('/projects'),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  delete: (id) => api.delete(`/projects/${id}`),
  createSample: (data) => api.post('/projects/create-sample', data || {}),
};

// Datasets
export const datasetsAPI = {
  upload: (formData) => api.post('/datasets/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  get: (id) => api.get(`/datasets/${id}`),
  listByProject: (projectId) => api.get(`/datasets/project/${projectId}`),
};

// Chat
export const chatAPI = {
  send: (data) => api.post('/chat', data),
  getHistory: (sessionId) => api.get(`/chat/history/${sessionId}`),
};

// Artifacts
export const artifactsAPI = {
  listBySession: (sessionId) => api.get(`/artifacts/session/${sessionId}`),
};

// Templates
export const templatesAPI = {
  list: (params) => api.get('/templates', { params }),
  get: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
};

// Feedback
export const feedbackAPI = {
  submit: (data) => api.post('/feedback', data),
};

// Admin
export const adminAPI = {
  // Core
  stats: () => api.get('/admin/stats'),
  enhancedStats: () => api.get('/admin/enhanced-stats'),
  // Users
  users: () => api.get('/admin/users'),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  getUserActivity: (id, limit = 50) => api.get(`/admin/users/${id}/activity?limit=${limit}`),
  resetPassword: (id, data) => api.post(`/admin/users/${id}/reset-password`, data),
  // Activity
  activity: (params) => api.get('/admin/activity', { params }),
  activitySummary: (days = 30) => api.get(`/admin/activity/summary?days=${days}`),
  // Analytics
  analyticsUsage: (days = 30) => api.get(`/admin/analytics/usage?days=${days}`),
  analyticsAgents: (days = 30) => api.get(`/admin/analytics/agents?days=${days}`),
  analyticsErrors: (days = 30) => api.get(`/admin/analytics/errors?days=${days}`),
  analyticsFunnel: () => api.get('/admin/analytics/funnel'),
  // Feedback
  feedback: (params) => api.get('/admin/feedback', { params }),
  updateFeedback: (id, data) => api.patch(`/admin/feedback/${id}`, data),
  // SMTP / Email
  getSmtp: () => api.get('/admin/smtp'),
  updateSmtp: (data) => api.patch('/admin/smtp', data),
  testSmtp: () => api.post('/admin/smtp/test'),
  sendEmail: (data) => api.post('/admin/email/send', data),
  // Waitlist
  waitlist: (params) => api.get('/admin/waitlist', { params }),
  updateWaitlist: (id, data) => api.patch(`/admin/waitlist/${id}`, data),
  inviteWaitlist: (id) => api.post(`/admin/waitlist/${id}/invite`),
  // Templates
  templates: () => api.get('/admin/templates'),
  deleteTemplate: (id) => api.delete(`/admin/templates/${id}`),
  // Blog & Announcements
  blog: () => api.get('/blog'),
  createBlog: (data) => api.post('/admin/blog', data),
  announcements: () => api.get('/announcements'),
  createAnnouncement: (data) => api.post('/admin/announcements', data),
  tickets: () => api.get('/admin/tickets'),
  // Config
  config: () => api.get('/admin/config'),
  updateConfig: (data) => api.patch('/admin/config', data),
};

export default api;
